import { Button } from '@/components/ui/Button';
import { ShowNotificationToastOptions, useNotificationToast } from '@/lib/hooks/useNotificationToast';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Cookies from 'universal-cookie';
import { pushSyncInfo } from '../../app/services/xtremePushSync';
import { asyncTrackingEvent } from '../../app/utils/tracking';
import withLocation from '../withLocation';

declare global {
  interface Window {
    xtremepush: Function;
  }
}

/**
 * Browser Notification Permission States
 */
enum PermStates {
  DEFAULT = 'default', // User has not yet allowed/ blocked and functionality is available.
  GRANTED = 'granted', // User has already allowed notifications permissions.
  DENIED = 'denied', // User has already blocked notifications permissions.
  UNAVAILABLE = 'unavailable' // Browser doesn't support.
}

const cookies = new Cookies();
const COOKIE_PUSH = 'PUSH_NOTIFICATIONS';
const DAYS_TO_PROMPT = 28;
const DELAY_TO_SHOW_PROMPT = 15000;

const exclusionRoutes = ['/login', '/register'];
let timer: any = null;

/**
 * Attempt to send user_id to push service
 * linking current device to user id.
 *
 * @param { string } userId
 */
const pushUserId = (userId: string) => {
  window.xtremepush('ready', function () {
    const deviceInfo = window.xtremepush('get', 'device_info');
    if (!deviceInfo.user_id || deviceInfo.user_id != userId) {
      window.xtremepush('set', 'user_id', userId);
      pushSyncInfo();
    }
  });
};

/**
 * Updates current notify permission state if changed, and returns
 *
 * @param {string} notifyPermissionState
 * @param {Function} setNotifyPermissionState
 *
 * @return {string}
 */
const updateNotifyPermissionState = (notifyPermissionState: string, setNotifyPermissionState: Function): string => {
  return window.xtremepush('ready', function () {
    const permission = window.xtremepush('push', 'permission');
    if (permission != notifyPermissionState) setNotifyPermissionState(permission);
  });
};

const requestNotifyPermission = (setPermissionState: Function, isSignedIn: boolean) => {
  // eslint-disable-next-line no-undef
  window.xtremepush('push', 'prompt', {
    allowCallback: function () {
      // User clicked allow.
      setPermissionState(PermStates.GRANTED);
    },
    denyCallback: function () {
      // User clicked deny.
    },
    dismissCallback: function () {
      // User dismissed dialog. It is possible to prompt again later
      promptDismissed(isSignedIn);
    }
  });
};

/**
 * Display custom notification prompt.
 *
 * @param {string} notifyPermissionState
 * @param {Function} setPermissionState
 * @param {boolean} isSignedIn
 */
const displayNotificationPrompt = (
  notifyPermissionState: string,
  setPermissionState: Function,
  isSignedIn: boolean,
  showNotificationToast: ({ type, title, message, content, config }: ShowNotificationToastOptions) => void
) => {
  if (notifyPermissionState == PermStates.DEFAULT && !promptHasBeenDismissed()) {
    const AcceptButtonText = 'Turn on notifications';
    const PostponeButtonText = 'Not now';
    const NotificationToastId = 'notification-prompt';

    asyncTrackingEvent({
      track: 'push_notification_impression',
      trackAbacus: 'push_notification_impression'
    });

    showNotificationToast({
      id: NotificationToastId,
      type: 'bare',
      content: (
        <div className="space-y-4">
          <p className="text-sm">
            Subscribe to our notifications to receive the latest jobs, events and careers advice.
          </p>
          <div className="flex gap-2 items-center justify-start flex-col sm:flex-row">
            <Button
              size="sm"
              appearance="primary"
              type="button"
              className="w-full"
              onClick={() => {
                requestNotifyPermission(setPermissionState, isSignedIn);
                asyncTrackingEvent({
                  track: 'push_notification_accept',
                  trackAbacus: 'push_notification_accept',
                  trackText: AcceptButtonText
                });
                toast.dismiss(NotificationToastId);
              }}
              data-cy={`button:accept-notification`}
            >
              {AcceptButtonText}
            </Button>
            <Button
              size="sm"
              appearance="background"
              type="button"
              className="w-full"
              onClick={() => {
                promptDismissed(isSignedIn);
                asyncTrackingEvent({
                  track: 'push_notification_postpone',
                  trackAbacus: 'push_notification_postpone',
                  trackText: PostponeButtonText
                });
                toast.dismiss(NotificationToastId);
              }}
              data-cy={`button:postpone-notification`}
            >
              {PostponeButtonText}
            </Button>
          </div>
        </div>
      ),
      config: {
        autoClose: false,
        position: 'top-right',
        closeOnClick: false
      },
      onClose: () => {
        asyncTrackingEvent({
          track: 'push_notification_close',
          trackAbacus: 'push_notification_close'
        });
      }
    });
  }
};

/**
 * Stores a cookie when prompt is dismissed
 *
 * @param {boolean} isSignedIn
 */
const promptDismissed = (isSignedIn: boolean) => {
  if (isSignedIn) {
    const date = new Date();
    date.setDate(date.getDate() + DAYS_TO_PROMPT);
    setPushCookie(date);
  } else {
    setPushCookie();
  }
};

/**
 * Creates a session cookie or one with an expiry date.
 *
 * @param {Date} expiry
 */
const setPushCookie = (expiry?: Date) => {
  if (expiry) {
    cookies.set(COOKIE_PUSH, true, {
      sameSite: 'strict',
      path: '/',
      expires: expiry
    });
  } else {
    cookies.set(COOKIE_PUSH, true, {
      sameSite: 'strict',
      path: '/'
    });
  }
};

/**
 * Based on cookie, returns whether prompt has been dismissed
 *
 * @return {Boolean}
 */
const promptHasBeenDismissed = (): boolean => {
  const pushCookie = cookies.get(COOKIE_PUSH);
  if (pushCookie) {
    return true;
  } else {
    return false;
  }
};

type Props = {
  userId: string | null;
  isSignedIn: boolean;
  location: any;
};

/**
 * Handles display of prompts to send notifications.
 *
 * @param {Props} param
 * @return {JSX}
 */
const NotificationHandler = ({ userId, isSignedIn, location }: Props) => {
  const { showNotificationToast } = useNotificationToast();
  const [notifyPermissionState, setNotifyPermissionState] = useState(PermStates.UNAVAILABLE);

  useEffect(() => {
    updateNotifyPermissionState(notifyPermissionState, setNotifyPermissionState);
  }, []);

  useEffect(() => {
    // If we already have a timer && permission is default
    if (notifyPermissionState == PermStates.DEFAULT && !timer && !exclusionRoutes.includes(location.pathname)) {
      timer = setTimeout(() => {
        displayNotificationPrompt(notifyPermissionState, setNotifyPermissionState, isSignedIn, showNotificationToast);
      }, DELAY_TO_SHOW_PROMPT);
    }
  }, [notifyPermissionState, location.pathname]);

  if (timer && exclusionRoutes.includes(location.pathname)) {
    clearTimeout(timer);
    timer = null;
  }

  useEffect(() => {
    if (notifyPermissionState == PermStates.GRANTED && userId) {
      pushUserId(userId);
    }
  }, [notifyPermissionState, userId]);

  return null;
};

export default withLocation(NotificationHandler);
